import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import RichTextEditor from '../../../components/RichTextEditor';

const EditTaskModal = ({ task, isOpen, onClose, onUpdate }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  
  // Состояние для редактирования
  const [editData, setEditData] = useState({
    site_id: '',
    d_start: '',
    d_end: '',
    pct: 0,
    status: 0,
    name: '',
    text: '',
  });

  // Обновляем данные формы при изменении задачи или открытии модального окна
  useEffect(() => {
    if (task && isOpen) {
      // Принудительно сбрасываем состояние перед установкой новых данных
      setEditData({
        site_id: '',
        d_start: '',
        d_end: '',
        pct: 0,
        status: 0,
        name: '',
        text: '',
      });
      
      // Устанавливаем новые данные с небольшой задержкой
      setTimeout(() => {
        setEditData({
          site_id: task.site_id || '',
          d_start: task.d_start || '',
          d_end: task.d_end || '',
          pct: task.pct || 0,
          status: task.status || 0,
          name: task.name || '',
          text: task.text || '',
        });
      }, 100);
      
      setError(null);
    }
  }, [task?.id, isOpen]); // Добавляем task.id в зависимости

  // Загружаем сайты при первой загрузке
  useEffect(() => {
    if (isOpen) {
      fetchSites();
    }
  }, [isOpen]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        console.error('Ошибка загрузки сайтов:', data.message);
      }
    } catch (error) {
      console.error('Ошибка загрузки сайтов:', error);
    } finally {
      setSitesLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(editData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onClose();
        if (onUpdate) {
          onUpdate(data.data);
        }
        showNotification(currentLang === 'ru' ? 'Задача обновлена!' : 'Task updated!', 'success');
      } else {
        throw new Error(data.message || 'Ошибка обновления задачи');
      }
    } catch (error) {
      console.error('Ошибка обновления задачи:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">
              {currentLang === 'ru' ? 'Редактировать задачу' : 'Edit Task'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={saving}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Название задачи */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Название задачи' : 'Task name'} *
                </label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                  disabled={saving}
                />
              </div>

              {/* Сайт */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Сайт' : 'Site'} *
                </label>
                <select
                  value={editData.site_id}
                  onChange={(e) => setEditData(prev => ({ ...prev, site_id: parseInt(e.target.value) || '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                  disabled={sitesLoading || saving}
                >
                  <option value="">
                    {sitesLoading 
                      ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') 
                      : (currentLang === 'ru' ? 'Выберите сайт' : 'Select site')
                    }
                  </option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.dns} {site.status === 1 ? '(Активен)' : '(Неактивен)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Дата начала */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Дата начала' : 'Start date'} *
                </label>
                <input
                  type="date"
                  value={editData.d_start}
                  onChange={(e) => setEditData(prev => ({ ...prev, d_start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                  disabled={saving}
                />
              </div>

              {/* Дата окончания */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Дата окончания' : 'End date'} *
                </label>
                <input
                  type="date"
                  value={editData.d_end}
                  onChange={(e) => setEditData(prev => ({ ...prev, d_end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                  disabled={saving}
                />
              </div>

              {/* Статус */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Статус' : 'Status'} *
                </label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData(prev => ({ ...prev, status: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                  disabled={saving}
                >
                  <option value={0}>{currentLang === 'ru' ? 'Ожидает' : 'Pending'}</option>
                  <option value={1}>{currentLang === 'ru' ? 'В работе' : 'In Progress'}</option>
                  <option value={2}>{currentLang === 'ru' ? 'Завершена' : 'Completed'}</option>
                  <option value={99}>{currentLang === 'ru' ? 'Отменена' : 'Cancelled'}</option>
                </select>
              </div>

              {/* Прогресс */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Прогресс' : 'Progress'} * ({editData.pct}%)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editData.pct}
                    onChange={(e) => setEditData(prev => ({ ...prev, pct: parseInt(e.target.value) || 0 }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    disabled={saving}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Описание */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLang === 'ru' ? 'Описание' : 'Description'}
              </label>
              <RichTextEditor
                value={editData.text || ''}
                onChange={(content) => setEditData(prev => ({ ...prev, text: content }))}
                currentLang={currentLang}
                placeholder={currentLang === 'ru' ? 'Введите описание задачи...' : 'Enter task description...'}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={saving}
            >
              {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
            >
              {saving ? (currentLang === 'ru' ? 'Сохранение...' : 'Saving...') : (currentLang === 'ru' ? 'Сохранить' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTaskModal; 